import * as Sentry from '@sentry/react';
import { setSharedErrorReporter } from '@shared/helpers/error-reporting.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import { NodonEnvironment } from '@shared/models/environment.model';
import type { IConfig } from '../providers/ConfigProvider';

/** Replay is a process-wide singleton; a second `replayIntegration()` throws. */
let hasStartedSentryInit = false;

const STACK_TAIL_FRAME_COUNT = 3;

/** Prefix the Amplitude browser SDK writes on its own `console.error` lines. */
const AMPLITUDE_LOGGER_MARKER = 'Amplitude Logger';

/**
 * Full request host: `amplitude.com` or a subdomain. Anchored so
 * `notamplitude.com`, `amplitude.company`, and `api2.amplitude.com.evil.com`
 * do not match.
 */
const AMPLITUDE_REQUEST_HOST_PATTERN = /^(?:[a-z0-9-]+\.)*amplitude\.com$/i;

type ErrorEventStackFrame = {
  filename?: string;
  function?: string;
  lineno?: number;
  colno?: number;
};

export type ErrorEventTypeKeyInput = {
  message?: string;
  fingerprint?: ReadonlyArray<string>;
  exception?: {
    values?: ReadonlyArray<{
      type?: string;
      value?: string;
      stacktrace?: {
        frames?: ReadonlyArray<ErrorEventStackFrame>;
      };
    }>;
  };
};

/**
 * Compact id for one stack frame used in {@link getErrorEventTypeKey}.
 */
const getFrameId = (frame: ErrorEventStackFrame): string =>
  [frame.filename, frame.function, frame.lineno, frame.colno]
    .map((part) => String(part ?? ''))
    .join(':');

/**
 * Session identity of an error: type, message, last stack frames, and fingerprint.
 * Events without an exception fall back to {@link ErrorEventTypeKeyInput.message}.
 * The stack tail keeps distinct call sites from collapsing when the message is generic.
 */
export const getErrorEventTypeKey = (event: ErrorEventTypeKeyInput): string => {
  const exception = event.exception?.values?.[0];
  const type = exception?.type ?? '';
  const value = exception?.value ?? event.message ?? '';
  const frames = exception?.stacktrace?.frames ?? [];
  const tail = frames.slice(-STACK_TAIL_FRAME_COUNT).map(getFrameId).join('|');
  const fingerprint = event.fingerprint?.join('|') ?? '';
  return [fingerprint, type, value, tail].join('::');
};

/**
 * Texts Sentry stores for a captured console error: the event message and
 * each exception value. Capture-console events use one or both.
 */
const getEventTexts = (event: ErrorEventTypeKeyInput): string[] => {
  const texts: string[] = [];
  if (guards.is.string(event.message)) texts.push(event.message);

  for (const exception of event.exception?.values ?? []) {
    if (guards.is.string(exception.value)) texts.push(exception.value);
  }

  return texts;
};

/**
 * Host of a `Failed to fetch (...)` target. A parenthetical URL contributes
 * its origin; a bare parenthetical is the host. Path and query are ignored.
 */
const getFailedFetchHost = (message: string): string | undefined => {
  const target = message.match(/Failed to fetch \(([^)]+)\)/)?.[1]?.trim();
  if (!target) return undefined;

  // A URL's origin is the request host; a bare parenthetical is the host itself.
  if (!target.includes('://')) return target.split('/')[0]?.split(':')[0];

  try {
    return new URL(target).hostname;
  } catch {
    return undefined;
  }
};

/**
 * True when the text is Amplitude SDK logger output, or a failed fetch whose
 * request host is `amplitude.com` or a subdomain. A mention of that host only
 * in a path or query does not match. Adblock, offline, and CSP produce these;
 * they are not product defects.
 */
const isAmplitudeLoggerNoiseMessage = (message: string): boolean => {
  if (message.includes(AMPLITUDE_LOGGER_MARKER)) return true;
  const host = getFailedFetchHost(message);
  if (!host) return false;
  return AMPLITUDE_REQUEST_HOST_PATTERN.test(host);
};

/**
 * True when a Sentry event's message or exception value is Amplitude logger noise.
 * {@link initClientSentry} checks this before session dedupe so the event is
 * dropped without taking a dedupe slot.
 */
export const isAmplitudeLoggerNoiseEvent = (
  event: ErrorEventTypeKeyInput,
): boolean => getEventTexts(event).some(isAmplitudeLoggerNoiseMessage);

/**
 * Returns a function that reports true for the second and later event of the
 * same {@link getErrorEventTypeKey} in this page session.
 */
export const createSessionErrorDeduper = () => {
  const seenKeys = new Set<string>();

  return (event: ErrorEventTypeKeyInput): boolean => {
    const key = getErrorEventTypeKey(event);
    if (seenKeys.has(key)) return true;
    seenKeys.add(key);
    return false;
  };
};

export type ShouldInitializeSentryClientInput = {
  environment: IConfig['environment'];
  existingClient: unknown;
};

/**
 * True when this tab should call {@link Sentry.init}.
 * Replay cannot be constructed twice; skip when a client is already present.
 */
export const shouldInitializeSentryClient = ({
  environment,
  existingClient,
}: ShouldInitializeSentryClientInput): boolean => {
  if (environment === NodonEnvironment.Localhost) return false;
  if (guards.is.defined(existingClient)) return false;
  return true;
};

export type InitClientSentryInput = {
  environment: IConfig['environment'];
  release: string;
};

/**
 * Initialize the browser Sentry SDK once per page lifetime.
 * `useInitSentry` can run again when App remounts or the shared route mounts.
 */
export const initClientSentry = ({
  environment,
  release,
}: InitClientSentryInput): void => {
  if (hasStartedSentryInit) return;
  if (
    !shouldInitializeSentryClient({
      environment,
      existingClient: Sentry.getClient(),
    })
  ) {
    return;
  }

  hasStartedSentryInit = true;
  const isSessionDuplicateError = createSessionErrorDeduper();

  Sentry.init({
    dsn: 'https://861cbd98d6ba439ba03140af69ca3a8a@o1180279.ingest.sentry.io/6299705',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.captureConsoleIntegration({
        levels: ['error'],
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: 1.0,
    // Full session sampling makes beforeErrorSampling a no-op; add it back if this rate drops
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
    environment,
    release,
    beforeSend: (event) => {
      // Drop analytics transport noise before dedupe so it is never counted as seen.
      if (isAmplitudeLoggerNoiseEvent(event)) return null;
      if (isSessionDuplicateError(event)) return null;
      return event;
    },
  });

  setSharedErrorReporter((error, extras) => {
    Sentry.captureException(error, { extra: extras });
  });
};
